/**
 * AISettingsCard — Versus AI chat singleton config + usage.
 *
 * Drops into SettingsPage.tsx as a sibling to "Default Betting Limits",
 * "Fees & Commissions", etc. Calls the backend admin endpoints added in
 * versusapp PR #87 (BE-AI-PROXY):
 *
 *   GET    /api/v1/admin/ai/settings
 *   PATCH  /api/v1/admin/ai/settings
 *   GET    /api/v1/admin/ai/usage?days=7
 *
 * The provider key never reaches the browser — only a boolean badge
 * (`keyConfigured`) sourced from process.env on the backend.
 */

import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import './AISettingsCard.css';

interface AIChatSettings {
  enabled: boolean;
  provider: 'deepseek' | 'openai' | 'anthropic';
  model: string;
  dailyCostCapCents: number;
  perUserMessagesPerHour: number;
  maxTokensPerRequest: number;
  updatedAt: string;
  updatedByAdminId: string | null;
  keyConfigured: boolean;
}

interface AIChatUsage {
  windowDays: number;
  totals: {
    total_cost_cents: number;
    total_tokens_in: number;
    total_tokens_out: number;
    total_requests: number;
  };
  dailySpendCents: number;
  topUsers: Array<{
    id: string;
    username: string;
    email: string;
    cost_cents: number;
    tokens_in: number;
    tokens_out: number;
    requests: number;
  }>;
}

const cents = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((n ?? 0) / 100);
const num = (n: number) => new Intl.NumberFormat('en-US').format(n ?? 0);

const AISettingsCard: React.FC = () => {
  const [settings, setSettings] = useState<AIChatSettings | null>(null);
  const [usage, setUsage] = useState<AIChatUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Local form state — flushed to backend on Save.
  const [enabled, setEnabled] = useState(false);
  const [model, setModel] = useState('');
  const [dailyCap, setDailyCap] = useState(0);
  const [perUserPerHour, setPerUserPerHour] = useState(0);
  const [maxTokens, setMaxTokens] = useState(0);

  const refresh = async () => {
    setError(null);
    try {
      const [s, u] = await Promise.all([
        apiService.getAIChatSettings(),
        apiService.getAIChatUsage(7, 10),
      ]);
      setSettings(s);
      setUsage(u);
      setEnabled(s.enabled);
      setModel(s.model);
      setDailyCap(s.dailyCostCapCents);
      setPerUserPerHour(s.perUserMessagesPerHour);
      setMaxTokens(s.maxTokensPerRequest);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Versus AI settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const dirty =
    !!settings &&
    (enabled !== settings.enabled ||
      model !== settings.model ||
      dailyCap !== settings.dailyCostCapCents ||
      perUserPerHour !== settings.perUserMessagesPerHour ||
      maxTokens !== settings.maxTokensPerRequest);

  const onSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await apiService.updateAIChatSettings({
        enabled,
        model,
        dailyCostCapCents: dailyCap,
        perUserMessagesPerHour: perUserPerHour,
        maxTokensPerRequest: maxTokens,
      });
      setSettings(updated);
      setSuccess('Saved.');
      window.setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-head">
          <span className="card-title">🤖 Versus AI</span>
        </div>
        <div className="card-body">
          <div className="ai-loading">Loading…</div>
        </div>
      </div>
    );
  }

  if (!settings || !usage) {
    return (
      <div className="card">
        <div className="card-head">
          <span className="card-title">🤖 Versus AI</span>
        </div>
        <div className="card-body">
          <div className="ai-error">{error || 'Failed to load.'}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <div className="card-head">
          <span className="card-title">🤖 Versus AI</span>
        </div>
        <div className="card-body">
          {error && <div className="ai-error">{error}</div>}
          {success && <div className="ai-success">{success}</div>}

          {/* Master toggle */}
          <div className="ai-row">
            <div className="ai-row-text">
              <div className="ai-row-title">Versus AI enabled</div>
              <div className="ai-row-sub">
                Master switch. When off, iOS hides the chat surface and the proxy returns 403.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              className={`ai-toggle ${enabled ? 'on' : 'off'}`}
              onClick={() => setEnabled((v) => !v)}
            >
              <span className="ai-toggle-knob" />
            </button>
          </div>

          {/* Provider + key state */}
          <div className="form-group">
            <label className="form-label">Provider</label>
            <input type="text" className="form-input" value={settings.provider} readOnly />
          </div>

          <div className="form-group">
            <label className="form-label">API Key (AWS Secrets Manager)</label>
            <div className={`ai-key-badge ${settings.keyConfigured ? 'ok' : 'missing'}`}>
              {settings.keyConfigured ? '✓ Configured' : '⚠ MISSING — set in AWS'}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Model</label>
            <input
              type="text"
              className="form-input"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="deepseek-v4-pro"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Daily cost cap (USD)</label>
            <input
              type="number"
              className="form-input"
              min={0}
              step={1}
              value={dailyCap / 100}
              onChange={(e) =>
                setDailyCap(Math.max(0, Math.round(parseFloat(e.target.value || '0') * 100)))
              }
            />
            <small className="form-hint">Currently {cents(dailyCap)} per day org-wide.</small>
          </div>

          <div className="form-group">
            <label className="form-label">Per-user messages per hour</label>
            <input
              type="number"
              className="form-input"
              min={0}
              value={perUserPerHour}
              onChange={(e) => setPerUserPerHour(Math.max(0, parseInt(e.target.value || '0', 10)))}
            />
            <small className="form-hint">0 blocks every user.</small>
          </div>

          <div className="form-group">
            <label className="form-label">Max tokens per response</label>
            <input
              type="number"
              className="form-input"
              min={1}
              max={32768}
              value={maxTokens}
              onChange={(e) =>
                setMaxTokens(Math.min(32768, Math.max(1, parseInt(e.target.value || '1', 10))))
              }
            />
          </div>

          <div className="ai-save-row">
            <small className="form-hint">
              Last updated{' '}
              {new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(
                new Date(settings.updatedAt)
              )}
              {settings.updatedByAdminId
                ? ` · admin ${settings.updatedByAdminId.slice(0, 8)}…`
                : ''}
            </small>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={!dirty || saving}
            >
              {saving ? 'Saving…' : dirty ? 'Save Changes' : 'No Changes'}
            </button>
          </div>
        </div>
      </div>

      <div className="card ai-usage-card">
        <div className="card-head">
          <span className="card-title">📊 Versus AI — Usage (last 7 days)</span>
          <span className="ai-today-spend">Today: {cents(usage.dailySpendCents)}</span>
        </div>
        <div className="card-body">
          <div className="ai-stats">
            <div className="ai-stat">
              <div className="ai-stat-label">Spend</div>
              <div className="ai-stat-value">{cents(usage.totals.total_cost_cents)}</div>
            </div>
            <div className="ai-stat">
              <div className="ai-stat-label">Requests</div>
              <div className="ai-stat-value">{num(usage.totals.total_requests)}</div>
            </div>
            <div className="ai-stat">
              <div className="ai-stat-label">Tokens in</div>
              <div className="ai-stat-value">{num(usage.totals.total_tokens_in)}</div>
            </div>
            <div className="ai-stat">
              <div className="ai-stat-label">Tokens out</div>
              <div className="ai-stat-value">{num(usage.totals.total_tokens_out)}</div>
            </div>
          </div>

          <table className="ai-usage-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th className="num">Requests</th>
                <th className="num">Tokens in/out</th>
                <th className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {usage.topUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="ai-empty">
                    No Versus AI usage yet.
                  </td>
                </tr>
              ) : (
                usage.topUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td className="muted">{u.email}</td>
                    <td className="num">{num(u.requests)}</td>
                    <td className="num muted">
                      {num(u.tokens_in)} / {num(u.tokens_out)}
                    </td>
                    <td className="num">{cents(u.cost_cents)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default AISettingsCard;
